// ----------------------------------------------------------------------------
// Component: Collection List Carousel Tabs
// ----------------------------------------------------------------------------
//
// Tabbed collection list sections reuse the blockified tabs controller from
// the tabbed-content section.
// ----------------------------------------------------------------------------

import Swiper from 'swiper';
import { Navigation, Scrollbar, A11y } from 'swiper/modules';
import 'snippets/card/card';
import 'sections/tabbed-content/tabbed-content';
import 'snippets/swiper-controls/swiper-control';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/scrollbar';
import 'swiper/css/a11y';
import './_collection-list.scss';
var TABBED_COLLECTION_LIST_SECTION_SELECTOR = '.section--collection-list.section--tabs';
var TAB_BUTTON_HORIZONTAL_SELECTOR = '.js-tab__button--horizontal';
var TAB_CONTENT_HORIZONTAL_SELECTOR = '.js-tab__content--horizontal';
var TAB_CAROUSEL_CONTAINER_SELECTOR = '.js-tab-collections-carousel';
var TAB_CAROUSEL_WRAPPER_SELECTOR = '.js-tab-collections-carousel-track';
var CAROUSEL_PREV_BUTTON_SELECTOR = '.swiper-button-prev';
var CAROUSEL_NEXT_BUTTON_SELECTOR = '.swiper-button-next';
var CAROUSEL_SCROLLBAR_SELECTOR = '.swiper-scrollbar';
var CAROUSEL_CONTROLS_SELECTOR = '.swiper-carousel__controls';
var CAROUSEL_CONTROLS_HIDDEN_CLASS = 'swiper-carousel__controls--hidden';
var OPEN_CLASS = 'is-open';
var DESKTOP_BREAKPOINT_PX = 768;
var DESKTOP_VISIBLE_SLIDES = 3;
function syncDesktopControlsVisibility(swiper, controlsEl) {
  if (!controlsEl) return;
  var isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT_PX;
  var shouldHideOnDesktop = isDesktop && (swiper.isLocked || swiper.slides.length <= DESKTOP_VISIBLE_SLIDES);
  controlsEl.classList.toggle(CAROUSEL_CONTROLS_HIDDEN_CLASS, shouldHideOnDesktop);
}
function initTabCarousel(carouselContainer) {
  if (carouselContainer.classList.contains('swiper-initialized')) return;
  var carouselWrapper = carouselContainer.querySelector(TAB_CAROUSEL_WRAPPER_SELECTOR);
  if (!carouselWrapper) return;
  var slideElements = Array.from(carouselWrapper.children).filter(slide => {
    var hasCardLink = slide.querySelector('.card__link');
    if (!hasCardLink) {
      slide.remove();
      return false;
    }
    return true;
  });
  if (slideElements.length === 0) return;
  carouselContainer.classList.add('swiper', 'carousel--collection-list');
  carouselWrapper.classList.add('swiper-wrapper');
  slideElements.forEach(slide => {
    slide.classList.remove('css-grid__item');
    slide.classList.add('swiper-slide');
  });
  var wrapperEl = carouselContainer.closest('.carousel-wrapper');
  var prevButton = wrapperEl === null || wrapperEl === void 0 ? void 0 : wrapperEl.querySelector(CAROUSEL_PREV_BUTTON_SELECTOR);
  var nextButton = wrapperEl === null || wrapperEl === void 0 ? void 0 : wrapperEl.querySelector(CAROUSEL_NEXT_BUTTON_SELECTOR);
  var scrollbarEl = wrapperEl === null || wrapperEl === void 0 ? void 0 : wrapperEl.querySelector(CAROUSEL_SCROLLBAR_SELECTOR);
  var controlsEl = wrapperEl === null || wrapperEl === void 0 ? void 0 : wrapperEl.querySelector(CAROUSEL_CONTROLS_SELECTOR);
  var swiper = new Swiper(carouselContainer, {
    modules: [Navigation, Scrollbar, A11y],
    slidesPerView: 1.5,
    observer: true,
    observeParents: true,
    a11y: true,
    watchOverflow: true,
    allowTouchMove: true,
    simulateTouch: true,
    grabCursor: true,
    touchStartPreventDefault: true,
    touchStartForcePreventDefault: true,
    breakpoints: {
      768: {
        slidesPerView: 3
      }
    },
    navigation: {
      nextEl: nextButton,
      prevEl: prevButton
    },
    scrollbar: {
      el: scrollbarEl,
      draggable: true,
      hide: false
    },
    on: {
      init(instance) {
        syncDesktopControlsVisibility(instance, controlsEl);
      },
      resize(instance) {
        syncDesktopControlsVisibility(instance, controlsEl);
      },
      breakpoint(instance) {
        syncDesktopControlsVisibility(instance, controlsEl);
      },
      update(instance) {
        syncDesktopControlsVisibility(instance, controlsEl);
      }
    }
  });
  syncDesktopControlsVisibility(swiper, controlsEl);
  swiper.slides.forEach((slide, slideIndex) => {
    var slideLink = slide.querySelector('a');
    slideLink === null || slideLink === void 0 || slideLink.addEventListener('focus', () => {
      swiper.slideTo(slideIndex);
    });
  });
  carouselContainer.querySelectorAll('img, a').forEach(node => {
    node.setAttribute('draggable', 'false');
  });
  carouselContainer.addEventListener('dragstart', event => event.preventDefault());
  carouselContainer.querySelectorAll('img').forEach(image => {
    image.addEventListener('load', () => {
      swiper.update();
    }, {
      once: true
    });
  });
}
function initPanelCarousel(panel) {
  var carouselContainer = panel === null || panel === void 0 ? void 0 : panel.querySelector(TAB_CAROUSEL_CONTAINER_SELECTOR);
  if (!carouselContainer) return;
  initTabCarousel(carouselContainer);
}
function initActiveTabCarousel(section) {
  var openPanels = section.querySelectorAll("".concat(TAB_CONTENT_HORIZONTAL_SELECTOR, ".").concat(OPEN_CLASS));
  if (openPanels.length > 0) {
    openPanels.forEach(panel => {
      initPanelCarousel(panel);
    });
    return;
  }
  initPanelCarousel(section.querySelector(TAB_CONTENT_HORIZONTAL_SELECTOR));
}
function bindTabActivationLazyInit(section) {
  if (section.dataset.tabCarouselLazyBound === 'true') return;
  section.dataset.tabCarouselLazyBound = 'true';
  section.addEventListener('click', event => {
    var button = event.target.closest(TAB_BUTTON_HORIZONTAL_SELECTOR);
    if (!button || !section.contains(button)) return;
    var panelId = button.getAttribute('aria-controls');
    if (!panelId) return;
    var panel = document.getElementById(panelId);
    if (!panel || !section.contains(panel)) return;
    initPanelCarousel(panel);
  });
}
function mountTabCarousels() {
  var root = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : document;
  root.querySelectorAll(TABBED_COLLECTION_LIST_SECTION_SELECTOR).forEach(section => {
    bindTabActivationLazyInit(section);
    initActiveTabCarousel(section);
  });
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountTabCarousels);
} else {
  mountTabCarousels();
}
document.addEventListener('shopify:section:load', event => {
  var isTabbedCollectionListSection = event.target.matches(TABBED_COLLECTION_LIST_SECTION_SELECTOR) || event.target.querySelector(TABBED_COLLECTION_LIST_SECTION_SELECTOR);
  if (isTabbedCollectionListSection) {
    mountTabCarousels(event.target);
  }
});